import axios, { type AxiosRequestConfig } from "axios";

// Generic result type for Axios requests
export interface AxiosResult<T> {
	success: boolean;
	data: T | null;
	validationErrors?: Record<string, string[]>;
	status?: number;
	message?: string;
	error?: any;
}

const axiosInstance = axios.create({
	baseURL: import.meta.env.VITE_SERVICE_CORE_URL,
	headers: {
		Accept: "application/json",
		"Content-Type": "application/json",
	},
	withCredentials: true,
});

// Extended config with option to preserve full response structure
interface ExtendedAxiosConfig extends AxiosRequestConfig {
	preserveFullResponse?: boolean;
}

// Reusable request utility with optional abort signal support
export async function axiosRequest<T>(config: ExtendedAxiosConfig): Promise<AxiosResult<T>> {
	try {
		const { preserveFullResponse, ...axiosConfig } = config;
		const response = await axiosInstance({ ...axiosConfig });
		// console.log("Axios Response:", response);

		// If preserveFullResponse is true, return the full response data (useful for paginated responses)
		const responseData = preserveFullResponse ? response.data : response.data && typeof response.data === "object" && "data" in response.data ? response.data.data : response.data;

		return {
			success: true,
			data: responseData as T,
			status: response.status,
		};
	} catch (error) {
		if (axios.isAxiosError(error)) {
			if (error.response) {
				const status = error.response.status;
				if (status === 422) {
					return {
						data: null,
						success: false,
						validationErrors: error.response.data.errors,
						status,
						message: error.response.data.message,
					};
				}
				return {
					data: null,
					success: false,
					status,
					message: error.response.data.message || "Server error",
					error: error.response.data,
				};
			} else if (error.request) {
				return {
					data: null,
					success: false,
					message: "No response from server",
					error,
				};
			} else {
				return {
					data: null,
					success: false,
					message: error.message,
					error,
				};
			}
		}
		return {
			data: null,
			success: false,
			message: "Unexpected error",
			error,
		};
	}
}
